<template>
  <!-- The corner every device surface hangs its automations in: the iconic
       indicator first (the switch/light timer ring, the shade's wind/rain/sun
       mark, a binary sensor's alarm shield), then one mini badge per automation
       that is not already drawn by it. This is the overlay compact tiles have
       always had; a card face hands the same device facts to the same
       components instead of a bare badge stack (owner, 24.10.2026: *"why not
       the same indicators iconic.. like we had on button type widgets"*). -->
  <div class="automation-overlay">
    <!-- A binary sensor leads with its alarm zone — the shield, the eye, the
         walking man its compact tile draws — because a `binary_sensor_alarm`
         badge could only say "an alarm is bound here"; the indicator says what
         that alarm is doing (armed / pending / detecting / fired). -->
    <div v-if="alarm.has" class="automation-alarm">
      <span v-if="alarm.label" class="automation-alarm__label" :class="alarm.labelClass">{{ alarm.label }}</span>
      <AlarmZoneIndicator
        :armed="alarm.armed"
        :alarm="alarm.alarm"
        :pending="alarm.pending"
        :supervise="alarm.supervise"
        :sensor-active="alarm.active" />
    </div>

    <SwitchAutomationIndicator v-if="switchAutomation" :automation="switchAutomation" :accent="accent" />
    <ShadeAutomationIndicator
      v-else-if="shade.has"
      :wind-state="shade.wind"
      :rain-state="shade.rain"
      :sun-state="shade.sun" />

    <AutomationBadges
      :device-id="deviceId"
      :device-values="deviceValues"
      :exclude-ids="switchAutomation ? [switchAutomation.automationId] : []"
      :exclude-types="[
        ...(shade.has ? SHADE_AUTOMATION_TYPES : []),
        ...(alarm.has ? BINARY_SENSOR_ALARM_TYPES : []),
      ]" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AutomationBadges from './AutomationBadges.vue';
import SwitchAutomationIndicator from './SwitchAutomationIndicator.vue';
import ShadeAutomationIndicator from './ShadeAutomationIndicator.vue';
import AlarmZoneIndicator from './AlarmZoneIndicator.vue';
import { useDeviceAutomationsStore } from '../../stores/deviceAutomations';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useSolar } from '../../composables/useSolar';
import { tt } from '../../utils/consoleText';
import {
  BINARY_SENSOR_STATE_KEYS,
  isBinarySensorValueActive,
  resolveBinarySensorActiveFromValues,
} from '@/utils/binarySensorState';
import {
  buildSwitchAutomationDisplay,
  getSwitchWidgetAutomationSummaries,
  toSunTimesLike,
  type SwitchAutomationDisplay,
} from '../../utils/switchAutomation';

/**
 * What the surface draws. It decides the leading indicator — a switch/light
 * reads its timer/motion automations, a cover its wind/rain/sun protections, a
 * binary sensor its alarm zone, and everything else shows the badge stack alone
 * (the same split the compact tiles make).
 */
export type DeviceAutomationKind = 'switch' | 'light' | 'cover' | 'binary-sensor' | 'other';

const props = withDefaults(
  defineProps<{
    deviceId: string;
    kind?: DeviceAutomationKind;
    /** Pass `device.deviceValues` so automation state is there on first paint. */
    deviceValues?: Array<{ valueKey: string; currentValue?: unknown }>;
    /** Overrides the indicator's own colour (the light's current colour). */
    accent?: string | null;
  }>(),
  { kind: 'other', deviceValues: () => [], accent: null },
);

const SHADE_AUTOMATION_TYPES = ['shade_wind_protection', 'shade_rain_protection', 'shade_sun_tracking'];

/** The sensor's own alarm automation — drawn by the zone indicator, never as a badge. */
const BINARY_SENSOR_ALARM_TYPES = ['binary_sensor_alarm'];

const automationsStore = useDeviceAutomationsStore();
const { currentValues, initializeValues } = useDeviceValues(props.deviceId);
const solar = useSolar();
const nowTick = ref(Date.now());

if (props.deviceValues?.length) initializeValues(props.deviceValues);
watch(
  () => props.deviceValues,
  (values) => {
    if (values?.length) initializeValues(values);
  },
  { deep: true },
);
watch(
  () => props.deviceId,
  (deviceId) => void automationsStore.fetchDeviceAutomationSummaries(deviceId),
);

// ── Switch / light: timer and motion automations ──────────────────────────
const switchAutomations = computed<SwitchAutomationDisplay[]>(() => {
  if (props.kind !== 'switch' && props.kind !== 'light') return [];
  const summaries = getSwitchWidgetAutomationSummaries(automationsStore.getDeviceAutomationSummaries(props.deviceId));

  return summaries
    .map((automation) =>
      buildSwitchAutomationDisplay(
        automation,
        currentValues.value,
        new Date(nowTick.value),
        toSunTimesLike(solar.sunTimes.value),
      ),
    )
    .filter((automation): automation is SwitchAutomationDisplay => automation !== null);
});

/** The automation the icon speaks for: the live one, else the first armed one. */
const switchAutomation = computed(
  () => switchAutomations.value.find((automation) => automation.isActive) ?? switchAutomations.value[0] ?? null,
);

// ── Cover: wind / rain / sun protections ──────────────────────────────────
const shade = computed(() => {
  if (props.kind !== 'cover') {
    return { has: false, wind: null, rain: null, sun: null } as const;
  }

  const read = (type: string, stateKey: string) => {
    const key = `${type}__${stateKey}`;
    return key in currentValues.value ? currentValues.value[key] : undefined;
  };
  const isTrue = (value: unknown) => value === true || value === 1 || value === 'true';
  const protection = (type: string) => {
    const active = read(type, 'protection_active');
    if (active === undefined) return null;
    if (isTrue(active)) return 'active' as const;
    return read(type, 'resume_time') ? ('resuming' as const) : ('idle' as const);
  };

  const wind = protection('shade_wind_protection');
  const rain = protection('shade_rain_protection');
  const sun = read('shade_sun_tracking', 'sun_active');
  const sunState = sun === undefined ? null : isTrue(sun) ? ('active' as const) : ('idle' as const);

  return { has: wind !== null || rain !== null || sunState !== null, wind, rain, sun: sunState };
});

// ── Binary sensor: the alarm zone ─────────────────────────────────────────
/**
 * The alarm `BinarySensorWidget` draws on its own tile and sheet: the hub writes
 * `<type>__armed` / `__supervise` / `__alarm` / `__pending_trigger_at` onto the
 * sensor, so a card needs no automation fetch to know the zone's state — only
 * the values the widget already reads. The reading itself comes from the same
 * shared fold (`binarySensorState`), which is what the walking man in supervise
 * mode follows.
 */
const alarm = computed(() => {
  const idle = {
    has: false,
    armed: false,
    alarm: false,
    pending: false,
    supervise: false,
    active: false,
    label: '',
    labelClass: '',
  } as const;
  if (props.kind !== 'binary-sensor') return idle;

  const has = 'binary_sensor_alarm__armed' in currentValues.value || 'armed' in currentValues.value;
  if (!has) return idle;

  const read = (short: string): unknown => {
    const prefixed = `binary_sensor_alarm__${short}`;
    if (prefixed in currentValues.value) return currentValues.value[prefixed];
    return currentValues.value[short];
  };
  const isTrue = (value: unknown) => value === true || value === 1 || value === 'true';

  const armed = isTrue(read('armed'));
  const supervise = isTrue(read('supervise'));
  const fired = isTrue(read('alarm'));
  const pendingAt = read('pending_trigger_at');
  const pending = pendingAt !== null && pendingAt !== undefined && pendingAt !== '';

  let active = false;
  for (const key of BINARY_SENSOR_STATE_KEYS) {
    if (key in currentValues.value) {
      active = isBinarySensorValueActive(currentValues.value[key]);
      break;
    }
  }
  if (!BINARY_SENSOR_STATE_KEYS.some((key) => key in currentValues.value)) {
    active = resolveBinarySensorActiveFromValues(props.deviceValues);
  }

  // The same words and tones the tile's own label uses (`BinarySensorWidget`).
  const label = fired
    ? tt('Alarm', 'Alarm')
    : pending && armed
      ? tt('Pending', 'V čakanju')
      : armed
        ? tt('Armed', 'Aktiviran')
        : supervise
          ? tt('Supervise', 'Nadzor')
          : '';
  const labelClass = fired
    ? 'text-red-500 dark:text-red-400'
    : pending
      ? 'text-amber-500 dark:text-amber-400'
      : armed
        ? 'text-green-500 dark:text-green-400'
        : supervise
          ? 'text-teal-500 dark:text-teal-400'
          : '';

  return { has, armed, alarm: fired, pending, supervise, active, label, labelClass };
});

// ── Clock ─────────────────────────────────────────────────────────────────
// The labels the indicator prints change on a minute, or on a second while a
// countdown runs; ticking at either grain only keeps the whole card from
// re-rendering once a second for text that could not have changed.
function advanceAutomationClock(): void {
  const now = Date.now();
  const minutePassed = Math.floor(now / 60_000) !== Math.floor(nowTick.value / 60_000);
  const countingDown = switchAutomations.value.some((automation) => automation.countdownText !== '');
  if (minutePassed || countingDown) nowTick.value = now;
}

let clock: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  void automationsStore.fetchDeviceAutomationSummaries(props.deviceId);
  clock = setInterval(advanceAutomationClock, 1000);
});

onBeforeUnmount(() => {
  if (clock !== null) clearInterval(clock);
  clock = null;
});
</script>

<style scoped>
/* The alarm row: the state word, then the indicator its tile draws. Metrics and
   the shadow follow `BinarySensorWidget`'s own `.alarm-indicator-label`, so the
   two surfaces print the same label (the colours are Tailwind's, per state). */
.automation-alarm {
  display: flex;
  align-items: center;
  gap: 3px;
}

.automation-alarm__label {
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
}
</style>
